import React from 'react';
import { Box, IconButton, Typography, SxProps, Theme } from '@mui/material';
import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import DateTimeDisplay from './DateTimeDisplay';
import { useServerLocalClock } from '../../hooks/useServerLocalClock';

export interface SubPageHeaderProps {
  title: React.ReactNode;
  onBack?: () => void;
  isHorizontal: boolean;
  /** Contenido a la derecha (chips, engranaje, etc.). En horizontal va junto al DateTimeDisplay. */
  rightContent?: React.ReactNode;
  /** Extra a la derecha de la flecha (p.ej. chip de estado en horizontal). */
  leftExtra?: React.ReactNode;
  /** DateTimeDisplay largo a la derecha en horizontal. Default true. */
  showHorizontalDateTime?: boolean;
  /** Fecha/hora compacta junto a la flecha en vertical. Default true. */
  showVerticalDateTime?: boolean;
  sx?: SxProps<Theme>;
  backButtonSx?: SxProps<Theme>;
}

/**
 * Header de páginas abiertas desde la barra inferior.
 * En vertical: flecha −20px, fecha/hora compacta del servidor entre flecha y título,
 * título centrado al ancho de pantalla.
 * En horizontal: layout previo (flecha | título | datetime + right).
 */
const SubPageHeader: React.FC<SubPageHeaderProps> = ({
  title,
  onBack,
  isHorizontal,
  rightContent,
  leftExtra,
  showHorizontalDateTime = true,
  showVerticalDateTime = true,
  sx,
  backButtonSx,
}) => {
  const { compactLabel } = useServerLocalClock({ enabled: !isHorizontal && showVerticalDateTime });

  const titleNode =
    typeof title === 'string' || typeof title === 'number' ? (
      <Typography
        sx={{
          fontSize: 18,
          fontWeight: 'bold',
          color: '#fff',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
        }}
      >
        {title}
      </Typography>
    ) : (
      title
    );

  const backBtn = onBack ? (
    <IconButton
      size="small"
      onClick={onBack}
      sx={{
        flexShrink: 0,
        color: '#2391FF',
        ...(!isHorizontal ? { ml: '-20px' } : {}),
        '& svg': { fontSize: 18 },
        ...backButtonSx,
      }}
    >
      <ArrowBackIosNew />
    </IconButton>
  ) : null;

  return (
    <Box
      sx={{
        paddingX: 2.5,
        paddingTop: 0.5,
        paddingBottom: 0.25,
        backgroundColor: '#000',
        borderBottom: '1px solid #333',
        position: 'sticky',
        top: 0,
        zIndex: 102,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        boxSizing: 'border-box',
        flexShrink: 0,
        gap: 1,
        ...sx,
      }}
    >
      {!isHorizontal ? (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              gap: 0.75,
              zIndex: 1,
              flex: '0 1 auto',
            }}
          >
            {backBtn}
            {showVerticalDateTime ? (
              <Typography
                component="span"
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 500,
                  color: '#ccc',
                  whiteSpace: 'nowrap',
                  letterSpacing: '-0.02em',
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {compactLabel}
              </Typography>
            ) : null}
          </Box>
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              /* Con fecha a la izq. / acciones a la der. el 42% evita solapes.
               * Sin eso (p.ej. Pileta: solo flecha) hay que dar más ancho o corta el título. */
              maxWidth: (!showVerticalDateTime && !rightContent) ? 'calc(100% - 72px)' : '42%',
              textAlign: 'center',
              zIndex: 0,
              pointerEvents: 'none',
              '& > *': { pointerEvents: 'auto' },
            }}
          >
            {titleNode}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              flexShrink: 0,
              zIndex: 1,
              ml: 'auto',
              // Compensa el ml:-20px de la flecha para que el último ícono quede al borde derecho
              mr: '-10px',
              gap: 0.25,
            }}
          >
            {rightContent}
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'center', minWidth: 0, gap: 0.5 }}>
            {backBtn}
            {leftExtra}
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minWidth: 0,
              textAlign: 'center',
            }}
          >
            {titleNode}
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              minWidth: 0,
              gap: 0.5,
            }}
          >
            {showHorizontalDateTime ? <DateTimeDisplay isHorizontal /> : null}
            {rightContent}
          </Box>
        </>
      )}
    </Box>
  );
};

export default SubPageHeader;
